import { useState, useEffect, useContext, useCallback } from "react";
import { apiFetch, useAuth } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { ThemeContext } from "../../../constant/theme";
import { LanguageContext } from "../../../constant/LanguageContext";
import { text } from "../constants/departmentsText";

const BASE_URL = process.env.REACT_APP_BASE_URL;

async function readJson(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error(body?.message || `Request failed (${res.status})`);
  }
  return body?.data ?? body;
}

export const useDepartmentsAdmin = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile, profileLoading } = useProfile();
  const { hasFeatureAccess } = useAuth();

  const t = text[language];
  const clientName = profile?.clientName || localStorage.getItem("clientName") || "";

  const can = {
    view: hasFeatureAccess("departments", "view"),
    add: hasFeatureAccess("departments", "add"),
    edit: hasFeatureAccess("departments", "edit"),
    delete: hasFeatureAccess("departments", "delete"),
    assignManagers: hasFeatureAccess("departments", "assignManagers"),
    merge: hasFeatureAccess("departments", "merge"),
  };

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "info" });

  const notifyError = useCallback((err) => {
    setAlertModal({ open: true, title: t.error, message: err?.message || String(err), variant: "error" });
  }, [t.error]);
  const notifySuccess = useCallback((message) => {
    setAlertModal({ open: true, title: t.success, message, variant: "success" });
  }, [t.success]);

  // ── companies dropdown (same source as Company Configuration) ──
  useEffect(() => {
    if (!clientName || !can.view) return;
    (async () => {
      try {
        const res = await apiFetch(`${BASE_URL}/api/company-config/${encodeURIComponent(clientName)}/detect-companies`);
        const data = await readJson(res);
        const raw = Array.isArray(data?.companies) ? data.companies : Array.isArray(data) ? data : [];
        const list = raw
          .map((c) => (typeof c === "string" ? c : c?.companyName))
          .filter((name) => typeof name === "string" && name.trim());
        setCompanies(list);
        if (list.length && !selectedCompany) setSelectedCompany(list[0]);
      } catch (err) {
        console.error("[Departments] detect-companies failed:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientName, can.view]);

  const fetchDepartments = useCallback(async () => {
    if (!clientName || !selectedCompany) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        clientName,
        workingCompany: selectedCompany,
        includeInactive: String(showInactive),
      });
      const res = await apiFetch(`${BASE_URL}/api/departments?${params}`);
      setDepartments(await readJson(res));
    } catch (err) {
      notifyError(new Error(`${t.loadError}: ${err.message}`));
    } finally {
      setLoading(false);
    }
  }, [clientName, selectedCompany, showInactive, notifyError, t.loadError]);

  useEffect(() => { fetchDepartments(); }, [fetchDepartments]);

  // ── mutations (each refetches on success) ──
  const createDepartment = async (payload) => {
    const res = await apiFetch(`${BASE_URL}/api/departments`, {
      method: "POST",
      body: JSON.stringify({ clientName, workingCompany: selectedCompany, ...payload }),
    });
    await readJson(res);
    notifySuccess(t.createSuccess);
    await fetchDepartments();
  };

  const updateDepartment = async (id, payload) => {
    const res = await apiFetch(`${BASE_URL}/api/departments/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    const data = await readJson(res);
    notifySuccess(t.renameSuccess);
    await fetchDepartments();
    return data;
  };

  const deactivateDepartment = async (id) => {
    const res = await apiFetch(`${BASE_URL}/api/departments/${id}`, { method: "DELETE" });
    await readJson(res);
    notifySuccess(t.deactivateSuccess);
    await fetchDepartments();
  };

  const mergeDepartments = async (targetId, sourceIds) => {
    const res = await apiFetch(`${BASE_URL}/api/departments/${targetId}/merge`, {
      method: "POST",
      body: JSON.stringify({ sourceIds }),
    });
    const data = await readJson(res);
    notifySuccess(t.mergeSuccess);
    await fetchDepartments();
    return data;
  };

  const setManagers = async (id, managers) => {
    const res = await apiFetch(`${BASE_URL}/api/departments/${id}/managers`, {
      method: "PUT",
      body: JSON.stringify({ managers }),
    });
    await readJson(res);
    notifySuccess(t.managersSuccess);
    await fetchDepartments();
  };

  const fetchMembers = async (id) => {
    const res = await apiFetch(`${BASE_URL}/api/departments/${id}/members`);
    return readJson(res);
  };

  return {
    theme, language, t, profileLoading, can,
    companies, selectedCompany, setSelectedCompany,
    departments, loading, showInactive, setShowInactive,
    alertModal, setAlertModal,
    fetchDepartments, createDepartment, updateDepartment, deactivateDepartment,
    mergeDepartments, setManagers, fetchMembers,
  };
};
